import axios from 'axios';
import { getDefaultStore } from 'jotai';
import { setAuthAtom, clearAuthAtom, tokenAtom } from '../../state/atoms/authAtoms';
import { CurrentUser, RegisterPayload, DecodedToken, UserTokenTypes } from '../../types/models';

const REACT_APP_API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";
const store = getDefaultStore();


const isTokenValid = (token: string): boolean => {
  try {
    const payload: DecodedToken = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp > currentTime;
  } catch (error) {
    console.error('Invalid token:', error);
    return false;
  }
};

const decodeToken = (token: string): DecodedToken => {
  try {
    const payload = token.split(".")[1];
    const decodedPayload = atob(payload);
    const parsedPayload: DecodedToken = JSON.parse(decodedPayload);

    if (parsedPayload.exp * 1000 < Date.now()) {
      throw new Error("Token has expired");
    }
    return parsedPayload;
  } catch (error) {
    console.error("Failed to decode token:", error);
    throw new Error("Invalid token");
  }
};

const register = async (payload: RegisterPayload) => {
  try {
    const response = await axios.post(
      `${REACT_APP_API_URL}/vcat-user/register`,
      payload,
      {
        headers: { "Content-Type": "application/json" },
      }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("Registration Error:", error.response?.data || error.message);
      throw new Error(error.response?.data?.detail || error.message);
    } else {
      console.error("Generic Registration Error:", (error as Error).message);
      throw error;
    }
  }
};

const login = async (identifier: string, password: string) => {
  try {
    const response = await axios.post(
      `${REACT_APP_API_URL}/auth/token`,
      { identifier, password }
    );

    const { access_token } = response.data;

    if (access_token && isTokenValid(access_token)) {
      const payload = JSON.parse(atob(access_token.split(".")[1]));
      const user: CurrentUser = {
        user_id: payload.user_id,
        username: payload.sub,
        role: payload.role,
        user_type: payload.user_type,
        account_root_id: payload.account_root_id,
        account_type: payload.account_type
      };
      localStorage.setItem("authToken", access_token);
      localStorage.setItem("currentUser", JSON.stringify(user));
      store.set(setAuthAtom, { user, token: access_token });

      return { user, token: access_token };
    } else {
      throw new Error("Invalid token received from server.");
    }
  } catch (error) {
    console.error("Login Error:", error);
    throw new Error("Login failed.");
  }
};


const logout = () => {
  localStorage.removeItem("authToken");
  localStorage.removeItem("currentUser");
  store.set(clearAuthAtom);
};

const getProtectedResource = async (endpoint: string) => {
  const token = store.get(tokenAtom) || localStorage.getItem("token");

  if (!token || !isTokenValid(token)) {
    throw new Error('Unauthorized: Invalid or expired token.');
  }

  try {
    const response = await axios.get(`${REACT_APP_API_URL}/${endpoint}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error('Protected Resource Error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.detail || error.message);
    } else {
      console.error('Generic Protected Resource Error:', (error as Error).message);
      throw error;
    }
  }
};
const forgotPassword = async (email: string) => {
  return fetch(`${REACT_APP_API_URL}/auth/forgot-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  }).then(async (res) => {
    if (!res.ok) {
      const errorData = await res.json();
      throw new Error(errorData.detail || "Failed to send reset email.");
    }
    return res.json();
  });
};


const resetPassword = async (reset_token: string, new_password: string) => {
  const response = await fetch(`${REACT_APP_API_URL}/auth/reset-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reset_token, new_password }),
  });

  if (!response.ok) {
    throw new Error("Failed to reset password.");
  }
};

export const getUserTypesFromToken = (): UserTokenTypes | null => {
  const token = localStorage.getItem("authToken");
  if (!token) return null;

  try {
    const decoded = decodeToken(token); // returns DecodedToken
    return {
      account_type: decoded.account_type,
      user_type: decoded.user_type,
    };
  } catch {
    return null;
  }
};






const authService = {
  register,
  login,
  logout,
  getProtectedResource,
  decodeToken,
  isTokenValid,
  forgotPassword,
  resetPassword,
  getUserTypesFromToken

};
export default authService;
